import React, { useEffect, useRef, useState, useMemo } from "react";
import { generateUniqueId } from "../../utils/comboBoxMultiSelectUtils.js";
import "../ComboBox/ComboBox.css";

const SelectActions = {
  Close: 0,
  CloseSelect: 1,
  First: 2,
  Last: 3,
  Next: 4,
  Open: 5,
  PageDown: 6,
  PageUp: 7,
  Previous: 8,
  Select: 9,
  Type: 10,
};

// Utility for generating unique IDs per component instance

function getActionFromKey(event, menuOpen) {
  const { key, altKey, ctrlKey, metaKey } = event;
  const openKeys = ["ArrowDown", "ArrowUp", "Enter", " "];

  if (!menuOpen && openKeys.includes(key)) return SelectActions.Open;
  if (key === "Home") return SelectActions.First;
  if (key === "End") return SelectActions.Last;
  if (
    key === "Backspace" ||
    key === "Clear" ||
    (key.length === 1 && key !== " " && !altKey && !ctrlKey && !metaKey)
  )
    return SelectActions.Type;

  if (menuOpen) {
    if (key === "ArrowUp" && altKey) return SelectActions.CloseSelect;
    else if (key === "ArrowDown" && !altKey) return SelectActions.Next;
    else if (key === "ArrowUp") return SelectActions.Previous;
    else if (key === "PageUp") return SelectActions.PageUp;
    else if (key === "PageDown") return SelectActions.PageDown;
    else if (key === "Escape") return SelectActions.Close;
    else if (key === "Enter") return SelectActions.CloseSelect;
    else if (key === " ") return SelectActions.Select;
  }
}

function getUpdatedIndex(currentIndex, maxIndex, action) {
  const pageSize = 10;
  switch (action) {
    case SelectActions.First:
      return 0;
    case SelectActions.Last:
      return maxIndex;
    case SelectActions.Previous:
      return Math.max(0, currentIndex - 1);
    case SelectActions.Next:
      return Math.min(maxIndex, currentIndex + 1);
    case SelectActions.PageUp:
      return Math.max(0, currentIndex - pageSize);
    case SelectActions.PageDown:
      return Math.min(maxIndex, currentIndex + pageSize);
    default:
      return currentIndex;
  }
}

const ComboBoxMultiSelect = ({
  label = "Favorite User",
  options,
  value,
  handleChange,
  handleFocusRef = () => null,
}) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  const comboRef = useRef(null);
  const listboxRef = useRef(null);

  // ✅ Unique ID for each instance
  const ids = useMemo(() => {
    const baseId = generateUniqueId("combo");
    return {
      combo: baseId,
      listbox: `${baseId}-listbox`,
      label: `${baseId}-label`,
      option: (i) => `${baseId}-option-${i}`,
    };
  }, []);

  const placeholder = label;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        comboRef.current &&
        !comboRef.current.contains(event.target) &&
        listboxRef.current &&
        !listboxRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (value) setSelectedKeys(value);
  }, [value]);

  const handleToggle = (key) => {
    setSelectedKeys((prev) => {
      const newSelected = prev.includes(key)
        ? prev.filter((v) => v !== key)
        : [...prev, key];
      handleChange(newSelected);
      return newSelected;
    });
  };

  const updateSearch = (char) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setSearch(""), 500);
    setSearch((s) => s + char);
  };

  const handleKeyDown = (e) => {
    if (open && e.key === "Tab") {
      e.preventDefault();
      setOpen(false);
      return;
    }

    const action = getActionFromKey(e, open);
    if (action == null) return;

    const max = options.length - 1;
    switch (action) {
      case SelectActions.First:
      case SelectActions.Last:
      case SelectActions.Next:
      case SelectActions.Previous:
      case SelectActions.PageUp:
      case SelectActions.PageDown: {
        e.preventDefault();
        setActiveIndex(getUpdatedIndex(activeIndex, max, action));
        break;
      }
      case SelectActions.Close:
        e.preventDefault();
        setOpen(false);
        break;
      case SelectActions.CloseSelect:
        e.preventDefault();
        handleToggle(options[activeIndex].key);
        setOpen(false);
        break;
      case SelectActions.Type:
        updateSearch(e.key);
        break;
      case SelectActions.Open:
        e.preventDefault();
        setOpen(true);
        break;
      case SelectActions.Select:
        e.preventDefault();
        handleToggle(options[activeIndex].key);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (search) {
      const idx = options.findIndex((opt) =>
        opt.value.toLowerCase().startsWith(search.toLowerCase())
      );
      if (idx >= 0) setActiveIndex(idx);
    }
  }, [search, options]);

  useEffect(() => {
    if (handleFocusRef && typeof handleFocusRef === "function") {
      handleFocusRef(comboRef.current);
    }
  }, [handleFocusRef]);

  useEffect(() => {
    if (open && listboxRef.current) {
      const activeEl = listboxRef.current.querySelector(
        `#${ids.option(activeIndex)}`
      );
      if (activeEl) activeEl.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, open, ids]);

  const selectedLabels = selectedKeys
    .map((k) => options.find((o) => o.key === k)?.value)
    .filter(Boolean);

  // const displayValue =
  //   selectedLabels.length === 0 ? placeholder : selectedLabels.join(", ");
   let displayValue;
    let ariaLabel;

    if (selectedLabels.length === 0) {
      displayValue = placeholder;
      ariaLabel = placeholder;
    } else if (selectedLabels.length > 2) {
      const extraCount = selectedLabels.length - 2;
      displayValue = `${selectedLabels.slice(0, 2).join(", ")} +${extraCount}`;
      // what NVDA will read aloud
      ariaLabel = `${selectedLabels.slice(0, 2).join(", ")} and ${extraCount} more selected`;
    } else {
      displayValue = selectedLabels.join(", ");
      ariaLabel = `${selectedLabels.join(", ")} selected`;
    }

  return (
    <div className={`combo multiCombo js-select ${open ? "open" : ""}`}>
      <div
        aria-controls={ids.listbox}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-labelledby={ids.label}
        id={ids.combo}
        className="combo-input"
        role="combobox"
        tabIndex={0}
        ref={comboRef}
        aria-activedescendant={open ? ids.option(activeIndex) : ""}
         aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={handleKeyDown}
      >
         {selectedLabels.length <= 2 ? (
            selectedLabels.join(", ") || placeholder
          ) : (
            <>
              {selectedLabels.slice(0, 2).join(", ")}
              &nbsp;
              <strong style={{marginLeft:'5px'}}>+{selectedLabels.length - 2}</strong> {/* BOLD PART */}
            </>
          )}
      </div>

      <div
        className="combo-menu"
        role="listbox"
        id={ids.listbox}
        aria-multiselectable="true"
        aria-labelledby={ids.label}
        ref={listboxRef}
        tabIndex={-1}
      >
        {options.map((opt, i) => (
          <div
            key={opt.key}
            id={ids.option(i)}
            role="option"
            aria-selected={selectedKeys.includes(opt.key)}
            className={`combo-option ${
              i === activeIndex ? "option-current" : ""
            }`}
            onClick={(e) => {
              e.stopPropagation();
              handleToggle(opt.key);
            }}
          >
            <input type="checkbox" readOnly checked={selectedKeys.includes(opt.key)} />
            <span>{opt.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ComboBoxMultiSelect;
